import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Lock, Mail, KeyRound, Check, ArrowLeft } from 'lucide-react';
import api from '../api/client';
import useThemeStore from '../stores/themeStore';
import nolagLogoWhite from '../assets/nolag-logo-white.svg';
import nolagLogoBlack from '../assets/nolag-logo-black.svg';

// Two-step self-service password reset for the SuperAdmin portal.
//   Step 1 ("request"): enter email -> POST /auth/password/forgot (emails a
//     6-digit OTP). The backend always returns {ok:true} so we can't leak
//     whether an account exists; we advance to step 2 regardless.
//   Step 2 ("reset"): enter the OTP + a new password -> POST /auth/password/reset.
// Endpoints live on the generic auth router (/api/auth/*), are CSRF-exempt, and
// resolve against the GLOBAL users table — so a superadmin/admin account works.
export default function ForgotPassword() {
  const navigate = useNavigate();
  const { theme } = useThemeStore();
  const logoSrc = theme === 'light' ? nolagLogoBlack : nolagLogoWhite;

  const [step, setStep] = useState('request');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await api.post('/auth/password/forgot', { email: email.trim() });
      // Always advances — response is uniform to avoid account enumeration.
      setStep('reset');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start the reset. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!otp.trim()) {
      setError('Enter the code from your email');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      await api.post('/auth/password/reset', {
        email: email.trim(),
        otp: otp.trim(),
        new_password: newPassword,
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 1800);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="forgot-page">
      <div className="forgot-bg">
        <div className="forgot-bg__gradient forgot-bg__gradient--1" />
        <div className="forgot-bg__gradient forgot-bg__gradient--2" />
      </div>

      <div className="forgot-container">
        {/* Logo lives INSIDE the card now, not floating above it — matches
            Login.jsx and primus-admin-main's login layout. Placed before
            the success/form branch so it shows in both states. */}
        <div className="forgot-card glass-card">
          <div className="forgot-logo">
            <img src={logoSrc} alt="NOLAG" className="forgot-logo__img" />
            <span className="forgot-logo__subtitle">SuperAdmin</span>
          </div>

          {success ? (
            <div className="forgot-success">
              <div className="forgot-success__icon"><Check size={32} /></div>
              <h2>Password reset</h2>
              <p>Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <div className="forgot-card__header">
                <h2 className="forgot-card__title">
                  {step === 'request' ? 'Reset your password' : 'Enter your code'}
                </h2>
                <p className="forgot-card__subtitle">
                  {step === 'request'
                    ? "Enter your account email and we'll send a 6-digit code."
                    : `We sent a code to ${email}. It expires in 10 minutes.`}
                </p>
              </div>

              {error && <div className="forgot-error"><span>{error}</span></div>}

              {step === 'request' ? (
                <form onSubmit={requestOtp} className="forgot-form">
                  <div className="input-group">
                    <label htmlFor="email" className="input-label">Email</label>
                    <div className="input-with-icon">
                      <Mail size={16} className="input-icon" />
                      <input
                        type="email"
                        id="email"
                        className="input"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(''); }}
                        placeholder="you@example.com"
                        autoComplete="email"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <button type="submit" className="btn btn--primary forgot-submit" disabled={isLoading || !email}>
                    {isLoading ? <><Loader2 size={18} className="spin" /> Sending…</> : <><Mail size={16} /> Send code</>}
                  </button>
                </form>
              ) : (
                <form onSubmit={resetPassword} className="forgot-form">
                  <div className="input-group">
                    <label htmlFor="otp" className="input-label">Verification code</label>
                    <div className="input-with-icon">
                      <KeyRound size={16} className="input-icon" />
                      <input
                        type="text"
                        id="otp"
                        className="input"
                        value={otp}
                        onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                        placeholder="6-digit code"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="input-group">
                    <label htmlFor="newPassword" className="input-label">New password</label>
                    <div className="input-with-icon">
                      <Lock size={16} className="input-icon" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="newPassword"
                        className="input"
                        value={newPassword}
                        onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                        disabled={isLoading}
                      />
                      <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="input-group">
                    <label htmlFor="confirmPassword" className="input-label">Confirm password</label>
                    <div className="input-with-icon">
                      <Lock size={16} className="input-icon" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        className="input"
                        value={confirmPassword}
                        onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                        placeholder="Re-enter new password"
                        autoComplete="new-password"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <button type="submit" className="btn btn--primary forgot-submit" disabled={isLoading || !otp || !newPassword || !confirmPassword}>
                    {isLoading ? <><Loader2 size={18} className="spin" /> Resetting…</> : <><Lock size={16} /> Reset password</>}
                  </button>

                  <button type="button" className="forgot-resend" onClick={() => { setStep('request'); setError(''); setOtp(''); }} disabled={isLoading}>
                    Didn't get a code? Try again
                  </button>
                </form>
              )}
            </>
          )}

          <div className="forgot-footer">
            <Link to="/login" className="forgot-back"><ArrowLeft size={14} /> Back to sign in</Link>
          </div>
        </div>
      </div>

      <style>{`
        .forgot-page {
          /* noLag rebrand: scoped to this page only, same treatment as
             Login.jsx — real NOLAG logo instead of the old hand-drawn
             blue/purple mark, background glow recolored to brand
             orange/red, accent scoped for the submit buttons. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);

          min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: var(--space-8); position: relative; overflow: hidden;
        }
        .forgot-bg { position: absolute; inset: 0; z-index: 0; }
        .forgot-bg__gradient { position: absolute; border-radius: 50%; filter: blur(100px); opacity: 0.5; }
        .forgot-bg__gradient--1 { width: 600px; height: 600px; top: -200px; right: -100px; background: radial-gradient(circle, rgba(254, 123, 0, 0.3) 0%, transparent 70%); }
        .forgot-bg__gradient--2 { width: 500px; height: 500px; bottom: -150px; left: -100px; background: radial-gradient(circle, rgba(247, 48, 47, 0.25) 0%, transparent 70%); }

        .forgot-container { position: relative; z-index: 1; width: 100%; max-width: 400px; display: flex; flex-direction: column; align-items: center; }
        .forgot-logo { display: flex; flex-direction: row; align-items: center; justify-content: center; gap: var(--space-3); margin-bottom: var(--space-8); }
        .forgot-logo__img { height: 68px; width: auto; max-width: 100%; display: block; }
        .forgot-logo__subtitle { font-size: var(--text-2xl); font-weight: 700; color: var(--accent-primary); letter-spacing: 0.01em; }

        .forgot-card { width: 100%; padding: var(--space-8); }
        .forgot-card__header { text-align: center; margin-bottom: var(--space-6); }
        .forgot-card__title { font-size: var(--text-xl); font-weight: 600; margin-bottom: var(--space-2); }
        .forgot-card__subtitle { font-size: var(--text-sm); color: var(--text-tertiary); }

        .forgot-error { padding: var(--space-4); background: var(--status-danger-subtle); border: 1px solid rgba(239, 68, 68, 0.2); border-radius: var(--radius-md); color: var(--status-danger); font-size: var(--text-sm); margin-bottom: var(--space-6); text-align: center; }

        .forgot-form { display: flex; flex-direction: column; gap: var(--space-5); }

        .input-with-icon { position: relative; }
        .input-with-icon .input { padding-left: 40px; padding-right: 48px; }
        .input-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--text-tertiary); pointer-events: none; }
        .password-toggle { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); padding: 6px; background: transparent; border: none; color: var(--text-tertiary); cursor: pointer; border-radius: var(--radius-sm); transition: color var(--duration-fast) var(--ease-out); }
        .password-toggle:hover { color: var(--text-primary); }

        .forgot-submit { width: 100%; padding: 14px; margin-top: var(--space-2); }
        .forgot-resend { background: transparent; border: none; color: var(--text-tertiary); font-size: var(--text-sm); cursor: pointer; padding: var(--space-2); }
        .forgot-resend:hover { color: var(--text-primary); }
        .forgot-resend:disabled { opacity: 0.5; cursor: not-allowed; }

        .forgot-footer { margin-top: var(--space-6); padding-top: var(--space-6); border-top: 1px solid var(--divider); text-align: center; }
        .forgot-back { display: inline-flex; align-items: center; gap: 6px; font-size: var(--text-sm); color: var(--text-tertiary); text-decoration: none; }
        .forgot-back:hover { color: var(--text-primary); }

        .forgot-success { text-align: center; padding: var(--space-4) 0; }
        .forgot-success__icon { width: 64px; height: 64px; margin: 0 auto var(--space-4); background: var(--status-success-subtle); color: var(--status-success); border-radius: 50%; display: flex; align-items: center; justify-content: center; }
        .forgot-success h2 { font-size: var(--text-xl); margin-bottom: var(--space-2); }
        .forgot-success p { color: var(--text-tertiary); font-size: var(--text-sm); }
      `}</style>
    </div>
  );
}
